import React from 'react';
import framePng from './images/frame.png';
import { toImgSrc } from '../../../../pages/CustomProgramPage/helpers';
import { NotLiveProfile } from './schema';
import { instagramLabel, youtubeLabel, tiktokLabel } from './helpers';
import styled from '@emotion/styled';
import { withAlpha } from '@client/new/helpers/cssHelpers';

const Tag = styled.span`
  color: ${(p) => p.color};
  background: ${(p) => withAlpha(p.color, 0.1)};
  border: 1px solid ${(p) => p.color};
  border-radius: 40px;
  font-size: 12px;
  white-space: nowrap;
  padding: 4px 10px;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Name = styled.div`
  font-size: 20px;
  line-height: 27px;
  font-weight: bold;
`;

const Title = styled.div`
  font-size: 13px;
  line-height: 20px;
`;

const Tab = styled.span`
  line-height: 20px;
  color: #151e33;
  opacity: 0.5;
  font-weight: 800;
  font-size: 15px;
`;

const Story = styled.p`
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 19px;
`;

const Accomplishments = styled.p`
  font-weight: 400;
  font-size: 13px;
  line-height: 19px;
`;

const Socials = styled.div`
  font-size: 11px;
  line-height: 15px;
  color: ${(p) => p.color};
  font-weight: bold;
`;

const Empty = styled.span`
  color: #151e33;
  opacity: 0.5;
`;

const PreviewWrapper = styled.div`
  position: fixed;
  margin-left: 1em;
  top: 66px;
`;

const StringPreview = ({
  value,
  name,
  maxLen,
}: {
  value: string;
  name: string;
  maxLen: number;
}) => {
  if (!value) return <Empty>{`Please fill out "${name}"`}</Empty>;
  const trimmed = value.slice(0, maxLen);
  return <>{`${trimmed}${value.length > trimmed.length ? '...' : ''}`}</>;
};

export function MobilePreview({ profile }: { profile: NotLiveProfile }) {
  const [tab, setTab] = React.useState(4);
  const tabStyle = (number: number) => ({
    opacity: tab === number ? 1 : 0.5,
    fontSize: '13px',
  });

  const color = `#${profile.customColorSchema?.primaryColor ?? '000'}`;

  return (
    <PreviewWrapper id="mobile-preview" className="hidden md:block">
      <div
        className="w-[374px] h-[764px] relative rounded-[60px] overflow-hidden scale-50 2xl:scale-100"
        style={{
          transformOrigin: '0% 0% 0px',
        }}
      >
        <img src={framePng} width="374px" className="absolute" />
        <div className="w-[340px] mx-auto bg-white h-full pt-3">
          <div
            className="h-[127px]"
            style={{
              backgroundImage: `url(${toImgSrc(profile.mediaBanner)})`,
              backgroundSize: 'contain',
              backgroundColor: color,
            }}
          ></div>
          <div className="relative px-3 pt-20">
            <div
              className="absolute rounded-full top-[-10px] w-[78px] h-[78px] bg-black"
              style={{
                border: '3px solid #fff',
                backgroundImage: `url(${toImgSrc(profile.mediaAvatar)})`,
                backgroundSize: 'cover',
              }}
            />
            <div className="">
              <Name>
                {profile.firstName || 'Name'} {profile.lastName || 'Surname'}
              </Name>
              <Title className="text-[10px] relative top-[-5px]">
                <StringPreview
                  value={profile['title']}
                  name="Title"
                  maxLen={50}
                />
              </Title>
              <Socials color={color} className="flex gap-6 mb-2">
                {profile.profileInstagram && instagramLabel}
                {profile.profileYoutubeChannel && youtubeLabel}
                {profile.profileTiktok && tiktokLabel}
              </Socials>
              <div className="flex text-[10px] font-bold space-x-1 gap-5">
                <Tab style={tabStyle(1)}>Collections</Tab>
                <Tab style={tabStyle(2)}>One-time Purchases</Tab>
                <Tab style={tabStyle(3)}>Insights</Tab>
                <Tab
                  style={{ ...tabStyle(4), borderBottom: '3px solid ' + color }}
                  onClick={() => setTab(4)}
                >
                  About
                </Tab>
              </div>
              <hr />
              <section className="mt-5">
                {profile.keywords?.length > 0 ? (
                  <TagsContainer>
                    {profile.keywords.map((k) => (
                      <Tag key={k} color={color}>
                        {k}
                      </Tag>
                    ))}
                  </TagsContainer>
                ) : (
                  <Empty>Add tags</Empty>
                )}
                <div className="mt-5">
                  <div className="text-[20px] font-bold">Story</div>
                  <Story className="text-sm">
                    <StringPreview
                      value={profile['messageProfile']}
                      name="Story"
                      maxLen={200}
                    />
                  </Story>
                </div>
                <div className="mt-5">
                  <div className="text-[20px] font-bold">Accomplishments</div>
                  <Accomplishments className="text-sm">
                    <StringPreview
                      value={profile['accomplishments']}
                      name="Accomplishments"
                      maxLen={200}
                    />
                  </Accomplishments>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </PreviewWrapper>
  );
}
